import {
  hasClearcutLikeIntervention,
  hasThinningLikeIntervention,
} from "../options";

type SnackbarCheckerProps = {
  key: string;
  value: any;
  parent?: string;
  prevData: any;
  largestArea?: number;
  enqueueSnackbar: (message: string, options?: { variant: string }) => void;
  translate: (key: string) => string;
};

type SnackbarRule = {
  key: string;
  condition: (value: any, prev: any) => boolean;
  message: string | string[];
};

// --- Config-based generic rules ---
const snackbarRules: SnackbarRule[] = [
  {
    key: "broadleavesToConifer",
    condition: (value, prev) => value === "yes" && prev.broadleavesToConifer !== "yes",
    message: "biomass-project-form-broadleaves-to-conifer-snackbar",
  },
  {
    key: "unevenAgedFoliage",
    condition: (value, prev) => value === "yes" && prev.unevenAgedFoliage !== "yes",
    message: "biomass-project-form-uneven-aged-foliage-snackbar",
  },
  {
    key: "hnvForestAbove9",
    condition: (value, prev) => value === "yes" && prev.hnvForestAbove9 !== "yes",
    message: "snackbar-biomass-project-form-hnv-over-9",
  },
  {
    key: "nbl",
    condition: (value, prev) => value === "yes" && prev.nbl !== "yes",
    message: "biomass-project-form-snackbar-nbl-selected",
  },
  {
    key: "heathland",
    condition: (value, prev) => value === "yes" && prev.heathland !== "yes",
    message: "biomass-project-form-snackbar-ineligible-for-ets",
  },
  {
    key: "undergrowthInOldForest",
    condition: (value, prev) => value === "yes" && prev.undergrowthInOldForest !== "yes",
    message: "snackbar-biomass-project-form-undergrowthInOldForest",
  },
  {
    key: "natura2000",
    condition: (value, prev) => value === "yes" && prev.natura2000 !== "yes",
    message: "biomass-project-form-snackbar-natura2000-selected",
  },
  {
    key: "natureValues",
    condition: (value, prev) => value === "field" && prev.natureValues !== "field",
    message: "biomass-project-form-nature-values-field-snackbar",
  },
  {
    key: "natureValues",
    condition: (value, prev) => value === "map" && prev.natureValues !== "map",
    message: "biomass-project-form-nature-values-map-snackbar",
  },
  // REMOVED: SBP maybe implemented later
  // {
  //   key: "type",
  //   condition: (value, prev) =>
  //     value === "energyWood" && !prev.riscAssessment?.includes("energyWood"),
  //   message: "biomass-project-form-supplementary-ortho2008-snackbar",
  // },
  {
    key: "appendix16Value",
    condition: (value, prev) =>
      ["3a", "4b", "4c", "4d"].includes(value) && prev.appendix16Value !== value,
    message: "biomass-project-form-snackbar-appendix-information",
  },
  {
    key: "appendix25Value",
    condition: (value, prev) =>
      ["3a", "4a", "5a "].includes(value) && prev.appendix25Value !== value,
    message: "biomass-project-form-snackbar-appendix-information",
  },
  // REMOVED: SBP maybe implemented later
  // {
  //   key: "type",
  //   condition: (value, prev) =>
  //     value === "agricultureResidues" && !prev.riscAssessment?.includes("agricultureResidues"),
  //   message: [
  //     "biomass-project-form-supplementary-ortho2008-snackbar",
  //     "biomass-project-form-supplementary-declarationPesticides-snackbar",
  //   ],
  // },
];

function handleArrayRules(
  value: string[],
  prev: any,
  enqueueSnackbar: (msg: string, opts?: { variant: string }) => void,
  translate: (key: string) => string,
) {
  const newItems = value.filter((v) => !prev.activity?.includes(v));
  if (hasClearcutLikeIntervention(newItems) || newItems.includes("clearing")) {
    enqueueSnackbar(
      translate("snackbar:biomass-project-form-snackbar-remember-removal-requirements-clearcut"),
      { variant: "info" },
    );

    if (prev.protectedForest === "no") {
      enqueueSnackbar(translate("biomass-project-form-snackbar-remember-restoration-agreement"), {
        variant: "info",
      });
    }
  }
  if (
    prev.category === "forest" &&
    (hasThinningLikeIntervention(newItems) || hasClearcutLikeIntervention(newItems))
  ) {
    enqueueSnackbar(
      translate("snackbar:biomass-project-form-snackbar-remember-removal-requirements-thinning"),
      { variant: "info" },
    );
  }
}

export function snackbarChecker({
  key,
  value,
  parent,
  prevData,
  largestArea,
  enqueueSnackbar,
  translate,
}: SnackbarCheckerProps) {
  // ✅ normalized previous values
  const prev = {
    activity: prevData?.standAndActivity?.activity ?? [],
    broadleavesToConifer: prevData?.standAndActivity?.broadleavesToConifer,
    unevenAgedFoliage: prevData?.standAndActivity?.unevenAgedFoliage,
    nbl: prevData?.natureAndConservation?.nbl,
    heathland: prevData?.natureAndConservation?.heathland,
    natureValues: prevData?.natureAndConservation?.natureValues,
    riscAssessment: prevData?.riscAssessment ?? [],
    protectedForest: prevData?.status?.protectedForest,
    appendix16Value: prevData?.standAndActivity?.appendix16Value,
    appendix25Value: prevData?.standAndActivity?.appendix25Value,
    category: prevData?.category,
  };

  const largeArea = (largestArea ?? 0) > 5;

  // --- Special clearcut rule ---
  if (
    parent === "standAndActivity" &&
    key === "activity" &&
    prev.category === "forest" &&
    hasClearcutLikeIntervention(value) &&
    !hasClearcutLikeIntervention(prev.activity) &&
    largeArea
  ) {
    enqueueSnackbar(translate("snackbar:biomass-screening-forest-clearcut-expert-review-warning"), {
      variant: "info",
    });
  }

  // --- Config-based rules ---
  for (const rule of snackbarRules) {
    if (key === rule.key && rule.condition(value, prev)) {
      const messages = Array.isArray(rule.message) ? rule.message : [rule.message];
      for (const msg of messages) {
        enqueueSnackbar(translate(msg), { variant: "info" });
      }
    }
  }

  // --- Array-type rules ---
  if (Array.isArray(value)) {
    handleArrayRules(value, prev, enqueueSnackbar, translate);
  }
}
